/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Import reflect-metadata for class-transformer
import "reflect-metadata";

// Global design tokens (brand gradients, radii, shadows, text colors)
import "@/styles/design-tokens.css";
// Shared layout utilities (full-page centred layouts)
import "@/styles/layout.css";
// Always-drawn scrollbars on table scroll containers (macOS hides overlay ones)
import "@/styles/table-scroll.css";

// Plugins
import { registerPlugins } from "@/plugins";

// Components
import App from "./App.vue";

// Composables
import { createApp } from "vue";
import { useAuthStore } from "@/store/auth";
import router from "@/router";
import { usageAnalytics } from "@/analytics/usageAnalytics";
import { useNotificationStore, type NotificationType } from "@/core/api/notificationStore";

import { getLogger } from "@/utils/logger";
import { installRequestIdInterceptor } from "@/utils/installRequestIdInterceptor";
import { installSessionIdInterceptor } from "@/utils/installSessionIdInterceptor";

const logger = getLogger("Main");

// Issue #1446: capture X-Request-Id off every backend response so error
// toasts and the E2E harness can correlate frontend failures to server
// log lines. Issue #1447: send X-Session-Id on every request so the
// server can stitch one visitor's journey across login + club switches.
// Install before any axios calls fire (auth bootstrap below is one).
installRequestIdInterceptor();
installSessionIdInterceptor();

const app = createApp(App);
registerPlugins(app);

// Initialize auth store and mount app after auth check is complete
const authStore = useAuthStore();

// First mount the app to ensure all plugins are initialized
app.mount("#app");

usageAnalytics.initialize(router);

// E2E-only navigation hook. A full page.goto in the Playwright harness
// re-boots the entire SPA (~0.5s of Vue/Vuetify JS); exposing router.push
// lets the harness navigate client-side instead, keeping the app mounted.
// `__thBootId` is stamped once per app boot so a test can prove a navigation
// stayed client-side (value survives) vs triggered a full reload (value
// changes). Mirrors the VITE_E2E hooks in meetingSubscriptionService.ts; no
// effect in production builds. See issue #2184.
if (import.meta.env.VITE_E2E) {
  // E2E-only test hooks bolted onto the global; not part of any typed surface.
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (window as any).__thNavigate = (path: string): Promise<void> =>
    router.push(path).then(
      () => undefined,
      () => undefined,
    );
  (window as any).__thBootId = Date.now();
  (window as any).__thQueueNotifications = (
    notifications: {
      type: NotificationType;
      message: string;
      requestId?: string;
      timeout?: number;
    }[],
  ): void => {
    const notificationStore = useNotificationStore();
    notificationStore.clear();
    for (const notification of notifications) {
      notificationStore.notify(notification.type, notification.message, {
        requestId: notification.requestId,
        timeout: notification.timeout,
      });
    }
  };
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

// Then check auth state - use force=true to ensure fresh data
authStore.checkAuth(true).finally(() => {
  void usageAnalytics.trackRouteView(router.currentRoute.value);
});

// Log auth state for debugging
logger.info("App initialized with auth store:", authStore.$id);
